import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, AlertTriangle, TrendingUp, Shield, BarChart3, DollarSign } from 'lucide-react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import bio from './images1/biogas.png';

const roiData = [
  { item: 'Daily CBG Output', value: '10,000 kg/day' },
  { item: 'Daily Revenue', value: '₹5.5 lakh' },
  { item: 'Monthly Revenue (30 days)', value: '₹1.65 crore' },
  { item: 'Annual Revenue', value: '₹20+ crore' },
  { item: 'Annual Monitoring Cost (RBHM)', value: '₹6 lakh (~0.3% of revenue)' },
];
// ✅ Framer Motion variants (MUST be above component)
const container = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: { staggerChildren: 0.15 }
  }
};

const item = {
  hidden: { opacity: 0, x: -20 },
  show: { opacity: 1, x: 0 }
};
const failureScenarios = [
  {
    scenario: 'A. Upset, slow recovery',
    production: 'Drops to 20% (30 days), then 80% (30 days)',
    duration: '60 days',
    revenueLost: '₹1.65 crore',
    profitLost: '₹33 lakh'
  },
  {
    scenario: 'B. Prolonged 50% output',
    production: '50% reduction',
    duration: '60 days',
    revenueLost: '₹1.65 crore',
    profitLost: '₹33 lakh'
  },
  {
    scenario: 'C. Severe shutdown',
    production: '100% loss',
    duration: '90 days',
    revenueLost: '₹4.95 crore',
    profitLost: '₹99 lakh'
  }
];

const comparisonData = [
  {
    item: 'Typical equipment cost (CAPEX)',
    gcHplc: 'GC-FID: ₹25-60 Lakh\nHPLC: ₹20-40 Lakh',
    rbhm: 'Field kit: ₹1-2 Lakh per site'
  },
  {
    item: 'Qualification of technician',
    gcHplc: 'M.Sc./B.Sc. (Chemistry/Biotech) with analytical lab experience',
    rbhm: 'Basic education (10th pass) + short practical training'
  },
  {
    item: 'Technician training (time)',
    gcHplc: '3-6 months (method validation)',
    rbhm: '2-7 days on-site practical training'
  },
  {
    item: 'Monthly technician pay',
    gcHplc: '₹40,000 – ₹80,000',
    rbhm: '₹8,000 – ₹25,000'
  },
  {
    item: 'Lab infrastructure cost',
    gcHplc: 'High (₹10-15 Lakh or more)',
    rbhm: 'Minimal (₹1-₹2 Lakh)'
  },
  {
    item: 'Consumables per test',
    gcHplc: 'High — ₹300 – ₹500+ / sample',
    rbhm: 'Very low — ₹20 – ₹50 / test'
  },
  {
    item: 'Time to actionable plan',
    gcHplc: 'Hours to days (24-72 hrs or more)',
    rbhm: 'Within hours (same day)'
  },
  {
    item: 'Annual maintenance / AMC',
    gcHplc: '8-15% of CAPEX (₹2–9 Lakh/yr)',
    rbhm: 'Low: ₹10k-80k/yr/site'
  }
];

export const CBGgas = () => {
  return (
    <div className="min-h-screen pt-20" data-testid="rbhm-page">
      {/* Hero Section */}
      <section className="bg-emerald-950 py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="text-amber-400 uppercase tracking-[0.3em] text-sm font-semibold">
              Compressed Biogas
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mt-4 mb-6">
              Commercial Biogas-CBG 
            </h1>
            <p className="text-white/70 text-lg max-w-3xl mx-auto tracking-wider leading-relaxed">
              Biogas–CBG plants are fully engineered, commercial-scale facilities designed for continuous production of Raw biogas and upgradation for long-term revenue generation.
            </p>
          </motion.div>
          
        </div>
      </section>
      
      {/* Overview */}
        <section className="py-12 md:py-10 bg-white dark:bg-emerald-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="flex justify-center mb-8"
          >
            <img
              src={bio}
              alt="Biogas Plant"
              className="w-full max-w-md h-auto rounded-lg shadow-xl"
            />
          </motion.div>
          <div className="grid lg:grid-cols-2 gap-16">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl font-bold text-emerald-900 dark:text-white mb-6">
                Commercial Biogas-CBG
              </h2>
              <div className="space-y-4 text-slate-600 dark:text-white/70 tracking-wider leading-relaxed">
                <p>
                 Biogas–CBG plants are fully engineered, commercial-scale facilities designed for continuous production of Raw biogas and upgradation for long-term revenue generation. RENVITEK Engineers LLP delivers comprehensive, end-to-end engineering and consulting solutions for the Biogas and Compressed Biogas (CBG) industry. Guided by our DIDA framework—Design, Implementation, Development, and Assessment—we support clients through every stage of the project lifecycle. Our core services include pre-feasibility studies and DPR preparation, waste analysis and substrate characterization, Biogas Generation Potential (BGP) and Biochemical Methane Potential (BMP) testing, biogas lab setup and inoculation services, process optimization and troubleshooting, and comprehensive O&M support for biogas and CBG plants.
                </p>
                <p>
                  RENVITEK possesses in-depth expertise in process design, engineering, and execution of bio biogas-CBG facilities, covering the complete value chain from feedstock characterization and digester hydraulics to gas handling and utilization. The company’s proprietary and customized process configurations enable efficient conversion of organic waste into biogas, followed by upgrading and enrichment of raw biogas into high-purity Bio-CNG, achieving methane concentrations of up to 98%, compliant with fuel-grade pecifications.
                </p>
                <p>
                  The upgraded Bio-CNG is suitable for direct thermal and energy applications, including replacement of LPG in commercial kitchens and hospitality facilities, industrial heating systems, and use as a clean vehicular fuel in substitution of conventional fossil fuels such as petrol and diesel, while also supporting energy transition goals.
                </p>
                <p>RENVITEK offers tailor-made technology solutions, engineered based on feedstock variability, plant capacity, operational constraints, and entrepreneur-specific commercial objectives. The company’s technical advisory and execution team comprises research professionals, process engineers, civil and mechanical engineers, and skilled technologists, ensuring multidisciplinary integration at every project stage.</p>
                <p>Continuous technology upgradation, performance monitoring, and applied research on diverse organic feedstocks form the core of RENVITEK’s innovation framework, enabling enhanced gas yields, improved process stability, and reduced operational risks—positioning the company as a technically competent and forward-looking solution provider in the Biogas CBG sector.</p>
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-emerald-50 dark:bg-emerald-900/50 p-8"
            >
              <div className="space-y-6">
                {[
                  {
                    title: '1. Project Management Consultation (PMC)',
                    items: [' Feasibility studies & site assessments', 'DPR preparation (SATAT/MNRE)', 'Techno-commercial advisory']
                  },
                  {
                    title: '2. Owner/Authority’s Engineer Services',
                    items: ['Acts as the Owner’s technical representative', ' Design & Technical Review', 'Construction Supervision & QC']
                  },
                  {
                    title: '3. Design & Engineering',
                    items: ['PFDs, P&IDs, layouts & specs', 'Customized AD & gas upgrading systems', 'Feedstock-specific pre-treatment & optimization']
                  },
                  {
                    title: '4. Project Execution',
                    items: ['Turnkey project delivery (concept commissioning)', ' Civil, mechanical, electrical & automation works', ' FAT, site inspections & quality checks']
                  },
                  {
                    title: '5. Process Optimization & R&D',
                    items: ['Pretreatments & yield improvement', ' Scum mitigation for high-solid feedstocks', 'Sensor, PLC & SCADA integration']
                  },
                  {
                    title: '6. Regulatory Assistance',
                    items: [' PESO, PCB & FSSAI approvals', 'LOI/PPA with OMCs', 'Subsidy, carbon credit & CSR support']
                  },
                  {
                    title: '7. Capacity Building',
                    items: ['Training programs & workshops', 'Manuals, documentation & lab protocols', 'Academic & R&D collaborations']
                  },
                  {
                    title: '8. Client & Vendor Coordination',
                    items: [' Client–vendor–stakeholder liaison', 'Vendor selection & BOQ analysis', 'Procurement & logistics management']
                  },
                  {
                    title: '9. O&M Support',
                    items: ['KPI monitoring, troubleshooting of digester & Upgradation systems', 'Operator training']
                  }
                ].map((section) => (
                  <div key={section.title}>
                    <h4 className="font-semibold text-emerald-900 dark:text-white mb-2">{section.title}</h4>
                    <ul className="space-y-1">
                      {section.items.map((item) => (
                        <li key={item} className="text-sm text-slate-600 dark:text-white/70 flex items-start gap-2">
                          <span className="text-amber-500 mt-1">•</span>
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

                {/* CTA Section */}
      <section className="py-12 md:py-10 bg-amber-500">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.h2
            className="text-3xl md:text-4xl font-bold text-emerald-950 mb-6"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            Ready to Transform Waste into Energy?
          </motion.h2>
          <motion.p
            className="text-emerald-900 text-lg mb-8"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
          >
            Contact us today to discuss how RENVITEK can help you achieve your sustainability goals.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
          >
            <Link to="/contact">
              <motion.button
                className="bg-emerald-900 hover:bg-emerald-800 text-white px-10 py-4 uppercase tracking-wider font-semibold flex items-center gap-2 mx-auto"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                data-testid="cta-contact-btn"
              >
                Contact Us Today
                <ArrowRight size={20} />
              </motion.button>
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default CBGgas;
